/**
 * Motor DIY / payment-retrigger helpers (hash router).
 * PG return host is /payment-status (referenceId + action=payment).
 * Share / Accept & Review Pay links stay on confirm-payment-diy.
 */

import { buildPaymentStatusRedirectUrl } from '@/lib/payment/payment-status-constants';
import { buildRouteUrl } from '@/mixins/util';

/** Same default as dashboard retrigger payment action (saveProposal status_id). */
export const DEFAULT_PAYMENT_RETRIGGER_STATUS_ID = 11;

/**
 * redirect_url value sent with saveProposal (payment retrigger / insurer submit).
 * PG returns here with referenceId & action=payment.
 */
export function buildProposalDiyReturnLink() {
    return buildPaymentStatusRedirectUrl();
}

/**
 * Optional transactionId from RM session (dashboard resumeLink).
 * @returns {string|null}
 */
function getRmTransactionId() {
    try {
        const raw = sessionStorage.getItem('rm_data');
        if (!raw) {
            return null;
        }
        const rm = JSON.parse(raw);
        const tid = rm?.transaction_id;
        return tid != null && tid !== '' ? String(tid) : null;
    } catch {
        return null;
    }
}

/**
 * Full shareable Accept & Review Pay URL for the customer (motor DIY page).
 * Requires resumeToken — journeyId-only deep links are not allowed.
 * @param {string|null|undefined} journeyId
 * @param {string|null|undefined} resumeToken
 * @returns {string}
 */
export function buildAcceptReviewPayUrl(journeyId, resumeToken) {
    const token = resumeToken != null && String(resumeToken).trim() !== ''
        ? String(resumeToken).trim()
        : '';
    if (!token) {
        return '';
    }
    let url = buildRouteUrl(`confirm-payment-diy?resumeToken=${encodeURIComponent(token)}`);
    const transactionId = getRmTransactionId();
    if (transactionId) {
        url += `&transactionId=${encodeURIComponent(transactionId)}`;
    }
    return url;
}

/**
 * Alias for parity with health naming where consumers expect "check DIY" URL builder.
 * @param {string|null|undefined} journeyId
 * @param {string|null|undefined} resumeToken
 * @returns {string}
 */
export function buildProposalDetailsCheckDiyUrl(journeyId, resumeToken) {
    return buildAcceptReviewPayUrl(journeyId, resumeToken);
}

/**
 * @param {string} text
 * @returns {Promise<void>}
 */
function writeTextToClipboard(text) {
    if (navigator.clipboard?.writeText) {
        return navigator.clipboard.writeText(text);
    }
    return new Promise((resolve, reject) => {
        try {
            const tempInput = document.createElement('input');
            tempInput.value = text;
            document.body.appendChild(tempInput);
            tempInput.select();
            document.execCommand('copy');
            document.body.removeChild(tempInput);
            resolve();
        } catch (e) {
            reject(e);
        }
    });
}

/**
 * Resolve resumeToken from a dashboard row (snake_case or camelCase).
 * @param {object|null|undefined} row
 * @returns {string|undefined}
 */
export function resumeTokenFromRow(row) {
    if (!row || typeof row !== 'object') {
        return undefined;
    }
    const token = row.resume_token ?? row.resumeToken;
    if (token == null || String(token).trim() === '') {
        return undefined;
    }
    return String(token).trim();
}

/**
 * GET resume-link for journey (no clipboard).
 *
 * @param {Function} backendApi - app mixin backendApi
 * @param {string|null|undefined} journeyId
 * @returns {Promise<{ ok: boolean, url?: string, message?: string }>}
 */
export async function fetchResumeLink(backendApi, journeyId) {
    if (journeyId === null || journeyId === undefined || String(journeyId).trim() === '') {
        return { ok: false, message: 'No journey reference available.' };
    }
    const id = String(journeyId).trim();
    try {
        const response = await backendApi('getResumeLink', [id], {}, 'get');
        if (response === 'error' || response?.status === 'error') {
            return { ok: false, message: 'Could not fetch Review and Pay link.' };
        }
        const errList = response?.data?.errors;
        if (Array.isArray(errList) && errList.length > 0) {
            const detail = errList[0]?.errorDisplayMessage;
            return {
                ok: false,
                message: detail || 'Could not fetch Review and Pay link.',
            };
        }
        const url = response?.data?.data?.resumeLink;
        if (!url || String(url).trim() === '') {
            return { ok: false, message: 'No resume link returned.' };
        }
        return { ok: true, url: String(url).trim() };
    } catch {
        return { ok: false, message: 'Could not fetch Review and Pay link.' };
    }
}

/**
 * Prefer backend getResumeLink; fall back to client-built Accept & Review Pay URL.
 *
 * @param {Function} backendApi - app mixin backendApi
 * @param {string|null|undefined} journeyId
 * @param {string|null|undefined} resumeToken
 * @returns {Promise<{ ok: boolean, url?: string, message?: string }>}
 */
export async function fetchResumeLinkOrBuild(backendApi, journeyId, resumeToken) {
    const result = await fetchResumeLink(backendApi, journeyId);
    if (result.ok && result.url) {
        return result;
    }
    const built = buildAcceptReviewPayUrl(journeyId, resumeToken);
    if (built) {
        return { ok: true, url: built };
    }
    return result;
}

/**
 * GET resume-link for journey (or client-built fallback), then copy to clipboard.
 *
 * @param {Function} backendApi - app mixin backendApi
 * @param {string|null|undefined} journeyId
 * @param {string|null|undefined} resumeToken
 * @returns {Promise<{ ok: boolean, url?: string, message?: string }>}
 */
export async function fetchAndCopyResumeLink(backendApi, journeyId, resumeToken) {
    const result = await fetchResumeLinkOrBuild(backendApi, journeyId, resumeToken);
    if (!result.ok || !result.url) {
        return result;
    }
    try {
        await writeTextToClipboard(result.url);
        return { ok: true, url: result.url };
    } catch {
        return { ok: false, url: result.url, message: 'Could not copy link to clipboard.' };
    }
}

/**
 * Calls saveProposal with the same payload shape as health dashboard retrigger.
 *
 * @param {Function} backendApi - app mixin backendApi
 * @param {string} journeyId
 * @param {number} statusId
 * @returns {Promise<*>}
 */
export function saveProposalPaymentRetrigger(backendApi, journeyId, statusId) {
    const qData = {
        journey_id: journeyId,
        status_id: statusId,
        redirect_url: buildProposalDiyReturnLink(),
        retrigger: true,
    };
    const headers = {
        Authorization: 'Basic ' + window.btoa(process.env.VUE_APP_USERNPASS),
        'Content-Type': 'application/json',
    };
    return backendApi('saveProposal', qData, headers);
}
